/**
 * Junto Frontend Button Component
 * 
 * This is the base button component that all Junto frontend systems use.
 * It maintains exact visual equivalence to the current Sol Room button
 * while being configurable for other studios.
 */

import React from 'react';
import styled from 'styled-components';
import { solRoomTheme } from '../../themes/sol-room.js';

// Use theme prop with fallback to solRoomTheme for backward compatibility
const StyledButton = styled.button`
  font-family: ${props => props.theme?.typography?.button?.fontFamily || solRoomTheme.typography.button.fontFamily};
  font-size: ${props => props.theme?.typography?.button?.fontSize || solRoomTheme.typography.button.fontSize};
  font-weight: ${props => props.theme?.typography?.button?.fontWeight || solRoomTheme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme?.typography?.button?.letterSpacing || solRoomTheme.typography.button.letterSpacing};
  text-transform: ${props => props.theme?.typography?.button?.textTransform || solRoomTheme.typography.button.textTransform};
  
  background: ${props => {
    if (props.$variant === 'filled') {
      return props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main;
    }
    return 'transparent';
  }};
  
  color: ${props => {
    if (props.$variant === 'filled') {
      return props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background;
    }
    return props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main;
  }};
  
  border: 1px solid ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  border-radius: 50px;
  
  padding: ${props => {
    const theme = props.theme || solRoomTheme;
    if (!theme || !theme.spacing) {
      // Fallback values if theme is still undefined
      switch (props.$size) {
        case 'small':
          return '8px 16px';
        case 'large':
          return '32px 64px';
        default:
          return '16px 32px';
      }
    }
    switch (props.$size) {
      case 'small':
        return `${theme.spacing.sm} ${theme.spacing.md}`;
      case 'large':
        return `${theme.spacing.lg} ${theme.spacing.xl}`;
      default:
        return `${theme.spacing.md} ${theme.spacing.lg}`;
    }
  }};
  
  cursor: pointer;
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  
  &:hover {
    background: ${props => {
      if (props.$variant === 'filled') {
        return props.theme?.colors?.accent?.deep || solRoomTheme.colors.accent.deep;
      }
      return props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main;
    }};
    
    color: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
    
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  &:disabled:hover {
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Button = ({ 
  children, 
  variant = 'outline', 
  size = 'medium', 
  fullWidth = false, 
  loading = false, 
  disabled = false,
  theme,
  onClick,
  ...props 
}) => {
  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      theme={theme}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {!loading && children}
    </StyledButton>
  );
};

export default Button;